import { motion, AnimatePresence } from 'framer-motion'
import {
  Bitcoin,
  Check,
  Clock,
  Copy,
  ExternalLink,
  RefreshCw,
  X,
} from 'lucide-react'
import { useEffect, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { QRCodeSVG } from 'qrcode.react'
import type { BookingData } from './PaymentChoiceModal'

interface BitcoinPaymentProps {
  isOpen: boolean
  onClose: () => void
  bookingData: BookingData
  btcAddress: string
}

interface BTCPrice {
  usd: number
  timestamp: number
}

export default function BitcoinPayment({
  isOpen,
  onClose,
  bookingData,
  btcAddress,
}: BitcoinPaymentProps) {
  const { t } = useTranslation('payment')
  const [btcPrice, setBtcPrice] = useState<BTCPrice | null>(null)
  const [btcAmount, setBtcAmount] = useState<string>('0')
  const [copiedField, setCopiedField] = useState<'amount' | 'address' | null>(
    null
  )
  const [timeLeft, setTimeLeft] = useState(30 * 60) // 30 minutes in seconds
  const [paymentStatus, setPaymentStatus] = useState<
    'pending' | 'checking' | 'confirmed'
  >('pending')
  const [error, setError] = useState<string | null>(null)

  // Fetch BTC price
  const fetchBTCPrice = useCallback(async () => {
    try {
      setError(null)
      // Try Blockchain.info first
      const response = await fetch('https://blockchain.info/ticker')
      if (response.ok) {
        const data = await response.json()
        const price = data.USD?.last
        if (price) {
          setBtcPrice({ usd: price, timestamp: Date.now() })
          const amount = (bookingData.totalPrice / price).toFixed(8)
          setBtcAmount(amount)
          return
        }
      }

      // Fallback to CoinGecko
      const cgResponse = await fetch(
        'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd'
      )
      if (cgResponse.ok) {
        const cgData = await cgResponse.json()
        const price = cgData.bitcoin?.usd
        if (price) {
          setBtcPrice({ usd: price, timestamp: Date.now() })
          const amount = (bookingData.totalPrice / price).toFixed(8)
          setBtcAmount(amount)
          return
        }
      }

      setError(t('errorPrice'))
    } catch (err) {
      console.error('Failed to fetch BTC price:', err)
      setError(t('errorPrice'))
    }
  }, [bookingData.totalPrice])

  // Initial price fetch
  useEffect(() => {
    if (isOpen) {
      fetchBTCPrice()
      setTimeLeft(30 * 60) // Reset timer
      setPaymentStatus('pending')
    }
  }, [isOpen, fetchBTCPrice])

  // Countdown timer
  useEffect(() => {
    if (!isOpen || paymentStatus === 'confirmed') return

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isOpen, paymentStatus])

  // Format time remaining
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Copy to clipboard
  const copyToClipboard = async (text: string, field: 'amount' | 'address') => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedField(field)
      setTimeout(() => setCopiedField(null), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  // Generate Bitcoin URI for QR code
  const bitcoinUri = `bitcoin:${btcAddress}?amount=${btcAmount}&label=TerraLao-${bookingData.name.replace(
    /\s/g,
    ''
  )}`

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  // Simulate payment check (in production, this would poll the blockchain)
  const checkPayment = async () => {
    setPaymentStatus('checking')
    // Simulate checking - in production, call your backend API
    setTimeout(() => {
      // For demo, just reset to pending
      setPaymentStatus('pending')
    }, 2000)
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-md"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 w-full max-w-md max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Card Container */}
            <div className="bg-gradient-to-b from-garage-dark to-[#1a1a1a] rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
              {/* Header */}
              <div className="relative bg-gradient-to-r from-[#f7931a] to-[#ff9d00] p-5">
                {/* Bitcoin pattern overlay */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.3)_0%,transparent_50%)]" />
                </div>

                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-xl border border-white/20">
                      <Bitcoin className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">
                        {t('bitcoinTitle')}
                      </h2>
                      <p className="text-white/80 text-sm">
                        {bookingData.activityTitle || 'Booking'} - $
                        {bookingData.totalPrice}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-white/20 rounded-xl transition-all duration-200 group"
                    aria-label={t('close')}
                  >
                    <X className="h-5 w-5 text-white/80 group-hover:text-white transition-colors" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 space-y-5">
                {/* Timer */}
                <div
                  className={`flex items-center justify-center gap-2 p-3 rounded-xl font-mono text-sm font-semibold ${
                    timeLeft < 300
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                      : 'bg-[#f7931a]/20 text-[#f7931a] border border-[#f7931a]/30'
                  }`}
                >
                  <Clock className="h-4 w-4" />
                  <span>
                    {timeLeft > 0
                      ? t('windowOpen', { time: formatTime(timeLeft) })
                      : t('windowExpired')}
                  </span>
                </div>

                {error ? (
                  <div className="text-center py-8">
                    <p className="text-red-400 mb-4">{error}</p>
                    <button
                      onClick={fetchBTCPrice}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#f7931a] text-white rounded-xl hover:bg-[#ff9d00] transition-colors font-semibold"
                    >
                      <RefreshCw className="h-4 w-4" />
                      {t('retry')}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* QR Code */}
                    <div className="flex justify-center">
                      <div className="bg-white p-4 rounded-2xl shadow-lg">
                        <QRCodeSVG
                          value={bitcoinUri}
                          size={180}
                          level="H"
                          includeMargin={false}
                          bgColor="#ffffff"
                          fgColor="#1a1a1a"
                        />
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-white/60">
                          {t('amountToSend')}
                        </span>
                        <button
                          onClick={() => copyToClipboard(btcAmount, 'amount')}
                          className="text-xs text-[#f7931a] hover:text-[#ff9d00] flex items-center gap-1.5 transition-colors"
                        >
                          {copiedField === 'amount' ? (
                            <>
                              <Check className="h-3.5 w-3.5" />
                              {t('copied')}
                            </>
                          ) : (
                            <>
                              <Copy className="h-3.5 w-3.5" />
                              {t('copy')}
                            </>
                          )}
                        </button>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-bold font-mono text-white">
                          {btcAmount}
                        </span>
                        <span className="text-lg text-[#f7931a] font-semibold">
                          BTC
                        </span>
                      </div>
                      <div className="text-sm text-white/50 mt-1">
                        ≈ ${bookingData.totalPrice} USD
                        {btcPrice && (
                          <span className="text-xs ml-2">
                            (1 BTC = ${btcPrice.usd.toLocaleString()})
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Address */}
                    <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-white/60">
                          {t('sendToAddress')}
                        </span>
                        <button
                          onClick={() => copyToClipboard(btcAddress, 'address')}
                          className="text-xs text-[#f7931a] hover:text-[#ff9d00] flex items-center gap-1.5 transition-colors"
                        >
                          {copiedField === 'address' ? (
                            <>
                              <Check className="h-3.5 w-3.5" />
                              {t('copied')}
                            </>
                          ) : (
                            <>
                              <Copy className="h-3.5 w-3.5" />
                              {t('copy')}
                            </>
                          )}
                        </button>
                      </div>
                      <div className="font-mono text-xs bg-black/30 p-3 rounded-lg break-all text-white/80 border border-white/5">
                        {btcAddress}
                      </div>
                    </div>

                    {/* Check Payment Button */}
                    <button
                      onClick={checkPayment}
                      disabled={paymentStatus === 'checking'}
                      className="w-full flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-[#f7931a] to-[#ff9d00] hover:from-[#ff9d00] hover:to-[#ffb040] disabled:from-[#f7931a]/50 disabled:to-[#ff9d00]/50 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-[#f7931a]/20"
                    >
                      {paymentStatus === 'checking' ? (
                        <>
                          <RefreshCw className="h-5 w-5 animate-spin" />
                          {t('checking')}
                        </>
                      ) : (
                        <>
                          <Check className="h-5 w-5" />
                          {t('sentPayment')}
                        </>
                      )}
                    </button>

                    {/* Instructions */}
                    <div className="space-y-2 text-xs text-white/50">
                      <p className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#f7931a]/20 text-[#f7931a] flex items-center justify-center text-[10px] font-bold">
                          1
                        </span>
                        <span>{t('step1')}</span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#f7931a]/20 text-[#f7931a] flex items-center justify-center text-[10px] font-bold">
                          2
                        </span>
                        <span>
                          {t('step2pre')}{' '}
                          <span className="text-[#f7931a] font-mono">
                            {btcAmount}
                          </span>{' '}
                          {t('step2post')}
                        </span>
                      </p>
                      <p className="flex items-start gap-2">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#f7931a]/20 text-[#f7931a] flex items-center justify-center text-[10px] font-bold">
                          3
                        </span>
                        <span>{t('step3')}</span>
                      </p>
                    </div>

                    {/* View on Blockchain */}
                    <a
                      href={`https://www.blockchain.com/btc/address/${btcAddress}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 text-xs text-white/40 hover:text-[#f7931a] transition-colors py-2"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      {t('viewBlockchain')}
                    </a>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  )
}
